import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Bell, Share, Plus, Smartphone } from 'lucide-react';
import OneSignal from 'react-onesignal';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';

import { DEFAULT_PROMPT_COPY, type PromptCopy } from '../../util/notificationsPromptTriggers';
import { clearFlag, isFlagFresh, setFlag } from '../../util/expiringFlag';
import { optInPush } from '../OneSignal/oneSignalSafe';

const PENDING_KEY = 'cys-pending-push-prompt';
const DISMISSED_KEY = 'cys-prompt-dismissed-at';
const PENDING_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const DISMISSED_TTL_MS = 30 * 24 * 60 * 60 * 1000;

const STAGE = { CLOSED: 'closed', INSTALL: 'install', PUSH: 'push' } as const;
type Stage = typeof STAGE[keyof typeof STAGE];

let openListeners: Array<(copy: PromptCopy, force: boolean) => void> = [];

/**
 * Open the enable-notifications modal with the supplied copy. Safe to call
 * from anywhere (Redux middleware, settings toggle, dev FAB). The modal
 * itself handles platform branching and idempotency.
 *
 * Pass `{ force: true }` for explicit user intent (e.g. the settings toggle),
 * so the modal opens even when this browser already has notification
 * permission - required to let an already-enabled user subscribe a second
 * browser or re-subscribe one that was opted out.
 */
export const triggerEnableNotificationsFlow = (
  copy: PromptCopy = DEFAULT_PROMPT_COPY,
  opts?: { force?: boolean },
) => {
  openListeners.forEach(listener => listener(copy, !!opts?.force));
};

const isStandalone = () => {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as { standalone?: boolean }).standalone === true
  );
};

const isIosSafari = () => {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent;
  const isIos = /iPad|iPhone|iPod/.test(ua);
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  return isIos && isSafari;
};

const browserPermissionState = (): 'default' | 'granted' | 'denied' | 'unknown' => {
  try {
    const native = (OneSignal.Notifications as { permissionNative?: unknown })?.permissionNative;
    if (native === 'granted' || native === 'denied' || native === 'default') return native;
    if (typeof window !== 'undefined' && 'Notification' in window) {
      return (window.Notification as { permission: 'default' | 'granted' | 'denied' }).permission;
    }
  } catch {}
  return 'unknown';
};

const shouldSkipPrompt = () => {
  const state = browserPermissionState();
  if (state === 'granted' || state === 'denied') return true;
  return isFlagFresh(DISMISSED_KEY, DISMISSED_TTL_MS);
};

const pickStage = (): Exclude<Stage, typeof STAGE.CLOSED> =>
  isIosSafari() && !isStandalone() ? STAGE.INSTALL : STAGE.PUSH;


const EnableNotificationsFlow = () => {
  const [stage, setStage] = useState<Stage>(STAGE.CLOSED);
  const [copy, setCopy] = useState<PromptCopy>(DEFAULT_PROMPT_COPY);

  useEffect(() => {
    const open = (next: PromptCopy, force: boolean) => {
      if (!force && shouldSkipPrompt()) return;
      setCopy(next);
      setStage(pickStage());
    };
    openListeners.push(open);
    return () => {
      openListeners = openListeners.filter(l => l !== open);
    };
  }, []);

  useEffect(() => {
    if (!isStandalone()) return;
    if (!isFlagFresh(PENDING_KEY, PENDING_TTL_MS)) return;
    if (shouldSkipPrompt()) return;
    clearFlag(PENDING_KEY);
    setStage(STAGE.PUSH);
  }, []);

  const close = () => {
    setFlag(DISMISSED_KEY);
    setStage(STAGE.CLOSED);
  };

  const onAcceptInstall = () => {
    setFlag(PENDING_KEY);
    setStage(STAGE.CLOSED);
  };

  const onAcceptPush = () => {
    optInPush();
    setStage(STAGE.CLOSED);
  };

  return (
    <Dialog open={stage !== STAGE.CLOSED} onOpenChange={open => !open && close()}>
      <DialogContent className="sm:max-w-md">
        {stage === STAGE.INSTALL ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading uppercase tracking-wide">
                <FormattedMessage id={copy.titleId} />
              </DialogTitle>
              <DialogDescription>
                <FormattedMessage id={copy.bodyId} />
              </DialogDescription>
            </DialogHeader>
            <ol className="space-y-3 py-2">
              <li className="flex items-start gap-3">
                <Share className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <span className="text-sm">
                  <FormattedMessage id="EnableNotificationsFlow.iosStep.share" />
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Plus className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <span className="text-sm">
                  <FormattedMessage id="EnableNotificationsFlow.iosStep.add" />
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Smartphone className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <span className="text-sm">
                  <FormattedMessage id="EnableNotificationsFlow.iosStep.openFromHome" />
                </span>
              </li>
            </ol>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="ghost" onClick={close}>
                <FormattedMessage id="EnableNotificationsFlow.notNow" />
              </Button>
              <Button onClick={onAcceptInstall}>
                <FormattedMessage id="EnableNotificationsFlow.gotIt" />
              </Button>
            </DialogFooter>
          </>
        ) : null}

        {stage === STAGE.PUSH ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading uppercase tracking-wide">
                <FormattedMessage id={copy.titleId} />
              </DialogTitle>
              <DialogDescription>
                <FormattedMessage id={copy.bodyId} />
              </DialogDescription>
            </DialogHeader>
            <div className="flex items-center gap-3 rounded-lg border border-border bg-accent/40 p-4">
              <Bell className="h-5 w-5 shrink-0 text-primary" />
              <p className="text-sm text-muted-foreground">
                <FormattedMessage id="EnableNotificationsFlow.browserPromptHint" />
              </p>
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="ghost" onClick={close}>
                <FormattedMessage id="EnableNotificationsFlow.notNow" />
              </Button>
              <Button onClick={onAcceptPush}>
                <FormattedMessage id="EnableNotificationsFlow.allow" />
              </Button>
            </DialogFooter>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default EnableNotificationsFlow;
